/**
 * My Rentals Page - Filmssavn
 * Shows user's rental history and active rentals
 */

import { createFooter } from '../components/footer.js';
import { createNavigation } from '../components/navigation.js';
import { api } from '../lib/api.js';
import { getCurrentLanguage, t } from '../lib/i18n.js';
import { isAuthenticated, login } from '../lib/keycloak.js';

export async function renderMyRentals() {
  const app = document.getElementById('app');
  app.innerHTML = '';

  // Navigation
  app.appendChild(await createNavigation());

  // Check authentication
  if (!isAuthenticated()) {
    app.innerHTML += `
      <div class="container" style="padding: 100px 0; text-align: center;">
        <div class="card" style="max-width: 600px; margin: 0 auto;">
          <h2 style="margin-bottom: 1rem;">🔒 ${t('watch_login_required')}</h2>
          <p style="margin-bottom: 2rem; color: var(--color-cloud-grey);">
            ${t('watch_login_message')}
          </p>
          <button class="btn btn-primary" onclick="window.keycloak_login()">${t('watch_sign_in')}</button>
        </div>
      </div>
    `;

    window.keycloak_login = login;
    // Footer
    app.appendChild(createFooter());
    return;
  }

  // Create page header
  const headerSection = document.createElement('section');
  headerSection.className = 'section';
  headerSection.style.paddingTop = '100px';
  headerSection.innerHTML = `
    <div class="container">
      <h1 style="margin-bottom: 1rem;">${t('rentals_title')}</h1>
      <p style="color: var(--color-cloud-grey); margin-bottom: 2rem;">
        ${t('rentals_subtitle')}
      </p>
    </div>
  `;
  app.appendChild(headerSection);

  // Create rentals container
  const rentalsSection = document.createElement('section');
  rentalsSection.className = 'section';
  rentalsSection.innerHTML = `
    <div class="container">
      <div id="rentals-loading" style="text-align: center; padding: 4rem;">
        <div class="spinner"></div>
        <p style="margin-top: 1rem; color: var(--color-cloud-grey);">${t('rentals_loading')}</p>
      </div>
    </div>
  `;
  app.appendChild(rentalsSection);

  // Fetch rentals
  try {
    // API client now automatically handles token refresh
    const { videos } = await api.getMyVideos();

    // Remove loading
    document.getElementById('rentals-loading').remove();

    if (!videos || videos.length === 0) {
      // No rentals
      rentalsSection.innerHTML = `
        <div class="container">
          <div class="card" style="text-align: center; padding: 4rem;">
            <h3 style="margin-bottom: 1rem;">${t('rentals_none_title')}</h3>
            <p style="color: var(--color-cloud-grey); margin-bottom: 2rem;">
              ${t('rentals_none_message')}
            </p>
            <a href="/browse" class="btn btn-primary" data-link>${t('rentals_browse')}</a>
          </div>
        </div>
      `;
      return;
    }

    // Separate active and expired rentals
    const now = Date.now();
    const activeRentals = videos.filter((v) => new Date(v.expiresAt).getTime() > now);
    const expiredRentals = videos.filter((v) => new Date(v.expiresAt).getTime() <= now);

    const currentLang = getCurrentLanguage();

    // Build rentals HTML
    let rentalsHTML = '<div class="container">';

    // Active rentals
    if (activeRentals.length > 0) {
      rentalsHTML += `
        <div style="margin-bottom: 3rem;">
          <h2 style="margin-bottom: 2rem;">${t('rentals_active')}</h2>
          <div class="grid-3">
      `;

      activeRentals.forEach((video) => {
        const title =
          currentLang === 'fo'
            ? video.originalTitle || video.internationalTitle
            : video.internationalTitle || video.originalTitle;
        const posterUrl = api.resolveAsset(video.posterUrl || video.metadata?.posterUrl) || '';
        const expiresAt = new Date(video.expiresAt);
        const timeLeft = getTimeRemaining(expiresAt);

        rentalsHTML += `
          <div class="rental-card">
            <div class="rental-poster">
              ${
                posterUrl
                  ? `
                <img src="${posterUrl}" alt="${title}" loading="lazy">
              `
                  : `
                <div class="rental-poster-placeholder">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-5-7l-3 3.72L9 13l-3 4h12l-4-5z"/>
                  </svg>
                </div>
              `
              }
              <div class="rental-status rental-status-active">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                </svg>
                ${t('rentals_status_active')}
              </div>
            </div>
            <div class="rental-info">
              <h3 class="rental-title">${title}</h3>
              <p class="rental-expires">${t('rentals_expires_in')} ${timeLeft}</p>
              <div class="rental-actions">
                <a href="/watch/${video.videoId}" class="btn btn-primary btn-sm" data-link>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style="margin-right: 4px;">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                  ${t('rentals_watch_now')}
                </a>
              </div>
            </div>
          </div>
        `;
      });

      rentalsHTML += `
          </div>
        </div>
      `;
    }

    // Expired rentals
    if (expiredRentals.length > 0) {
      rentalsHTML += `
        <div>
          <h2 style="margin-bottom: 2rem; opacity: 0.7;">${t('rentals_expired')}</h2>
          <div class="grid-3">
      `;

      expiredRentals.forEach((video) => {
        const title =
          currentLang === 'fo'
            ? video.originalTitle || video.internationalTitle
            : video.internationalTitle || video.originalTitle;
        const posterUrl = api.resolveAsset(video.posterUrl || video.metadata?.posterUrl) || '';
        const expiredDate = new Date(video.expiresAt).toLocaleDateString();

        rentalsHTML += `
          <div class="rental-card rental-card-expired">
            <div class="rental-poster">
              ${
                posterUrl
                  ? `
                <img src="${posterUrl}" alt="${title}" loading="lazy" style="opacity: 0.5;">
              `
                  : `
                <div class="rental-poster-placeholder" style="opacity: 0.5;">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-5-7l-3 3.72L9 13l-3 4h12l-4-5z"/>
                  </svg>
                </div>
              `
              }
              <div class="rental-status rental-status-expired">${t('rentals_status_expired')}</div>
            </div>
            <div class="rental-info">
              <h3 class="rental-title" style="opacity: 0.7;">${title}</h3>
              <p class="rental-expires" style="opacity: 0.7;">${t('rentals_expired_on')} ${expiredDate}</p>
              <div class="rental-actions">
                <a href="/watch/${video.videoId}" class="btn btn-secondary btn-sm" data-link>
                  ${t('rentals_rent_again')}
                </a>
              </div>
            </div>
          </div>
        `;
      });

      rentalsHTML += `
          </div>
        </div>
      `;
    }

    rentalsHTML += '</div>';
    rentalsSection.innerHTML = rentalsHTML;

    // Footer
    app.appendChild(createFooter());
  } catch (error) {
    console.error('Failed to load rentals:', error);
    document.getElementById('rentals-loading').innerHTML = `
      <div class="card" style="max-width: 600px; margin: 0 auto; text-align: center;">
        <h3 style="margin-bottom: 1rem;">${t('rentals_error_title')}</h3>
        <p style="color: var(--color-cloud-grey); margin-bottom: 2rem;">
          ${t('rentals_error_message')}
        </p>
        <button class="btn btn-primary" onclick="window.location.reload()">${t('rentals_retry')}</button>
      </div>
    `;

    // Footer
    app.appendChild(createFooter());
  }
}

/**
 * Calculate time remaining until expiration
 */
function getTimeRemaining(expiresAt) {
  const now = new Date();
  const diff = expiresAt - now;

  if (diff <= 0) return t('rentals_status_expired');

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  const dayText = days === 1 ? t('rentals_time_day') : t('rentals_time_days');
  const hourText = hours === 1 ? t('rentals_time_hour') : t('rentals_time_hours');
  const minuteText = minutes === 1 ? t('rentals_time_minute') : t('rentals_time_minutes');

  if (days > 0) return `${days} ${dayText}, ${hours} ${hourText}`;
  if (hours > 0) return `${hours} ${hourText}, ${minutes} ${minuteText}`;
  return `${minutes} ${minuteText}`;
}
